import React from 'react';

import 'utils/chartjsAdapterDayjs';
import Loader from 'components/common/Loader';
import ErrorFallback from 'components/modals/ErrorFallback';
import FeatureFlagProvider from 'contexts/FeatureFlagContext/FeatureFlagProvider';
import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { initDatadog, logError } from 'utils/datadog';

import App from './App';
import { persistor, store } from './store';
import './index.css';

import 'react-day-picker/dist/style.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-horizontal-scrolling-menu/dist/styles.css';

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('Unknown error');

dayjs.extend(advanced);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(duration);

// Init dataDog
initDatadog();

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate persistor={persistor} loading={<Loader isVisible />}>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error, info) => {
            const normalizedError = normalizeError(error);
            logError(normalizedError, { ...info, componentStack: 'ErrorBoundary' });
          }}
        >
          <FeatureFlagProvider>
            <App />
          </FeatureFlagProvider>
        </ErrorBoundary>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
